import React, { useEffect, useState, useRef } from "react";
import Carousel from "react-multi-carousel";
import { useSelector, useDispatch, useStore } from "react-redux";
import "react-multi-carousel/lib/styles.css";
import styles from "./ProductSliderComponent.module.css";
import ProductImage from "../../../common/ProductImage/ProductImage";
import { getImageUrl } from "../../../../utils/imageUtils";
import { getDelivery } from "../../../../utils/deliveryDateUtils";
import { getBatchUnifiedDeliveryData } from "../../../../lib/unifiedDeliveryService"; // Add this import
import { Add as AddIcon } from "@mui/icons-material"; // Import the Material UI icon
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"; // Import forward arrow icon
import ProductSliderSkeleton from "../ProductSliderSkeleton/ProductSliderSkeleton"; // Import the skeleton component
import { DateService } from "../../../../utils/dateUtils";
import { addToCart } from "../../../../lib/addToCart"; // Import addToCart function

interface ProductSliderProps {
  products: {
    id: string;
    name: string;
    image: string;
    price: string;
    newPrice?: string | null;
    discountAmount?: string | null;
    priceRange?: string | null;
    colors?: string[];
    url: string;
    hasChildren: boolean;
    fullProduct: any;
  }[];
  onAddToCart?: (product: any) => void; // Make this optional
  sliderItems: number;
  toggleMiniCart?: () => void; // Add this prop to receive miniCart toggle function
}

const transformProducts = async (
  products: any[],
  destinationLeadTimeZoneId?: string,
  destinationMethodsZoneId?: string
) => {
  // If location data is missing, return basic product information without delivery data
  if (!destinationLeadTimeZoneId || !destinationMethodsZoneId) {
    console.log("No location data available, skipping delivery calculations");
    return products.map((product) => ({
      id: product.id,
      name: product.name,
      image: product.images[0]?.small
        ? `${product.images[0]?.small}`
        : getImageUrl(), // Use getImageUrl utility for consistent placeholder
      centralPrice: product.price.discount
        ? `Ahora S/ ${product.price.discount.newPrice.toFixed(2)}`
        : product.price.regPrice
        ? (product.type !== "simple" ? "Desde S/ " : "S/ ") +
          product.price.regPrice.toFixed(2)
        : null,
      oldPrice: product.price.discount
        ? `S/ ${product.price.regPrice.toFixed(2)}`
        : null,
      priceRange: product.price.priceRange
        ? `Opciones entre S/ ${product.price.priceRange.min.toFixed(
            2
          )} - S/ ${product.price.priceRange.max.toFixed(2)}`
        : null,
      fastestDeliveryDate: null,
      colors: product.attributes
        .filter((attr: any) => attr.name === "Color")
        .flatMap((attr: any) =>
          attr.values.map((value: any) => value.colorHex)
        ),
      url: `/p/${product.url || product.id}`,
      hasChildren: product.attributes?.length > 0,
      fullProduct: {
        ...product,
        url: product.url || product.id, // Ensure we have the URL for the MiniCart
        sku: product.sku || `product-${product.id}`, // Ensure we have SKU
        images:
          product.images?.map((img: any) => ({
            small: img.small || img.url || "",
            thumbnail: img.thumbnail || img.small || img.url || "",
            original: img.original || img.large || img.url || img.small || "",
            url: img.url || img.small || "",
          })) || [],
      },
      expressDelivery: false,
    }));
  }

  // Prepare combinations for batch request
  const deliveryCombinations = products.map((product) => ({
    originLeadTimeZoneId:
      product.partner.originZones.deliveryLeadTimeZone.documentId,
    originFeeZoneId: product.partner.originZones.deliveryFeeZone.documentId,
    destinationLeadTimeZoneId,
    destinationFeeZoneId: destinationMethodsZoneId,
  }));

  // Fetch all delivery data in a single batch request
  const deliveryDataResults = await getBatchUnifiedDeliveryData(
    deliveryCombinations
  );

  // Transform products with the unified results
  return products.map((product, index) => {
    const imageUrl = product.images[0]?.small;
    let centralPrice, oldPrice, priceRange;
    const isDiscounted = product.price.discount;

    // Pricing logic
    if (product.type === "simple") {
      if (isDiscounted) {
        centralPrice = `Ahora S/ ${product.price.discount.newPrice.toFixed(2)}`;
        oldPrice = `S/ ${product.price.regPrice.toFixed(2)}`;
      } else {
        centralPrice = `S/ ${product.price.regPrice.toFixed(2)}`;
        oldPrice = null;
      }
      priceRange = null;
    } else {
      if (isDiscounted) {
        centralPrice = `Ahora S/ ${product.price.discount.newPrice.toFixed(2)}`;
        oldPrice = `S/ ${product.price.regPrice.toFixed(2)}`;
      } else {
        centralPrice = `Desde S/ ${product.price.priceRange.min.toFixed(2)}`;
        oldPrice = null;
      }
      priceRange =
        product.price.priceRange.min !== product.price.priceRange.max
          ? `Opciones entre S/ ${product.price.priceRange.min.toFixed(
              2
            )} - S/ ${product.price.priceRange.max.toFixed(2)}`
          : null;
    }

    // Colors mapping
    const colors = product.attributes
      .filter((attr: any) => attr.name === "Color")
      .flatMap((attr: any) => attr.values.map((value: any) => value.colorHex));

    // Extract the data from the unified delivery response
    const deliveryData = deliveryDataResults[index];
    const methods = deliveryData.deliveryMethods;
    const opsLeadTime = deliveryData.opsLeadTime;

    const deliveryOptions = {
      expressDelivery: product.logistics?.expressDelivery,
      limitTime: product.logistics?.limitTime,
      daysToDeliver:
        product.logistics?.minDaysToDeliver ?? product.logistics?.daysToDeliver,
      daysOpen: product.partner.daysOpen,
    };

    const deliveryDates = getDelivery(
      { ...deliveryOptions, opsLeadTime },
      new Date(),
      {
        express: !!methods.express,
        rapido: !!methods.rapido,
        standard: !!methods.standard,
      }
    );

    let fastestDeliveryDate = null;
    if (deliveryDates.express.isAvailable) {
      fastestDeliveryDate = `¡Recíbelo hoy mismo! Te quedan ${deliveryDates.express.countdown}`;
    } else if (deliveryDates.rapido.isAvailable) {
      const rapidoDate = deliveryDates.rapido.availableDates[0];
      fastestDeliveryDate = `Recíbelo ${
        rapidoDate
          ? `el ${DateService.formatDeliveryDateForDisplay(rapidoDate)}`
          : "entre mañana y pasado"
      }. Te quedan ${deliveryDates.rapido.countdown}`;
    } else if (deliveryDates.standard.startDate) {
      // Use the DateService to ensure consistent date formatting
      fastestDeliveryDate = `Recíbelo a partir del ${DateService.formatDeliveryDateForDisplay(
        deliveryDates.standard.startDate
      )}`;
    }

    return {
      id: product.id,
      name: product.name,
      image: imageUrl ? `${imageUrl}` : getImageUrl(), // Use getImageUrl utility
      centralPrice,
      oldPrice,
      priceRange,
      fastestDeliveryDate,
      colors,
      url: `/p/${product.url || product.id}`,
      hasChildren: product.attributes?.length > 0,
      fullProduct: {
        ...product,
        url: product.url || product.id, // Ensure we have the URL for the MiniCart
        sku: product.sku || `product-${product.id}`, // Ensure we have SKU
        images:
          product.images?.map((img: any) => ({
            small: img.small || img.url || "",
            thumbnail: img.thumbnail || img.small || img.url || "",
            original: img.original || img.large || img.url || img.small || "",
            url: img.url || img.small || "",
          })) || [],
      },
      expressDelivery: deliveryDates?.express?.isAvailable,
    };
  });
};

const ProductSlider: React.FC<ProductSliderProps> = ({
  products,
  onAddToCart,
  sliderItems,
  toggleMiniCart,
}) => {
  const [formattedProducts, setFormattedProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});
  const [allContentReady, setAllContentReady] = useState(false);
  const destinationLeadTimeZoneId = useSelector(
    (state: any) => state.location.deliveryLeadTimeZone?.documentId
  );
  const destinationMethodsZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  );

  // Get dispatch and store for internal add-to-cart functionality
  const dispatch = useDispatch();
  const store = useStore();

  console.log("products:", products);

  const isFetchingRef = useRef(false);

  // Track image loading state
  const handleImageLoad = (productId: string) => {
    setImagesLoaded((prev) => ({
      ...prev,
      [productId]: true,
    }));
  };

  // Add error handling for images to prevent layout shift on image error
  const handleImageError = (productId: string) => {
    setImagesLoaded((prev) => ({
      ...prev,
      [productId]: true,
    }));
  };

  // Utility to set tabindex="-1" on all focusable elements inside hidden carousel items
  const setTabIndexOnHiddenCarouselItems = () => {
    const focusableSelectors = [
      "a",
      "button",
      "input",
      "select",
      "textarea",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");
    const hiddenItems = document.querySelectorAll(
      '.react-multi-carousel-item[aria-hidden="true"]'
    );
    hiddenItems.forEach((item) => {
      const focusables = item.querySelectorAll(focusableSelectors);
      focusables.forEach((el) => {
        el.setAttribute("tabindex", "-1");
      });
    });
  };

  // Track when all content is ready to display
  useEffect(() => {
    if (!isLoading && Object.keys(imagesLoaded).length > 0) {
      // Check if all images have loaded (or errored)
      const allLoaded = Object.values(imagesLoaded).every((loaded) => loaded);
      if (allLoaded) {
        // Short delay to ensure font is loaded
        const timer = setTimeout(() => {
          setAllContentReady(true);
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, imagesLoaded]);

  useEffect(() => {
    let isMounted = true;

    const fetchFormattedProducts = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setIsLoading(true);

      try {
        console.log("Products being passed to transformProduct:", products);
        const results = await transformProducts(
          products,
          destinationLeadTimeZoneId,
          destinationMethodsZoneId
        );
        if (isMounted) {
          setFormattedProducts(results);
        }
      } catch (error) {
        console.error("Error fetching formatted products:", error);
      } finally {
        isFetchingRef.current = false;
        if (isMounted) setIsLoading(false);
      }
    };

    if (products.length > 0) {
      fetchFormattedProducts();
    } else {
      setIsLoading(false); // Set loading to false if there are no products
    }

    return () => {
      isMounted = false;
    };
  }, [products, destinationLeadTimeZoneId, destinationMethodsZoneId]);

  useEffect(() => {
    // Also set tabindex on initial mount/content update
    setTimeout(() => {
      setTabIndexOnHiddenCarouselItems();
    }, 0);
  }, [formattedProducts, isLoading, sliderItems]);

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 1024 },
      items: sliderItems,
      slidesToSlide: 1,
      draggable: false, // Disable dragging on desktop
    },
    desktop: {
      breakpoint: { max: 1024, min: 768 },
      items: sliderItems,
      slidesToSlide: 1,
      draggable: false, // Disable dragging on desktop
    },
    tablet: {
      breakpoint: { max: 768, min: 464 },
      items: Math.min(sliderItems, 2),
      slidesToSlide: 1,
      draggable: true, // Enable dragging on tablet
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 2.5,
      slidesToSlide: 1,
      draggable: true, // Enable dragging on mobile
    },
  };

  const CustomLeftArrow = ({ onClick }: any) => (
    <button
      onClick={onClick}
      className={`${styles.customArrow} ${styles.leftArrow}`}
      aria-label="Previous slide"
    >
      <ArrowForwardIosIcon style={{ transform: "rotate(180deg)" }} />
    </button>
  );

  const CustomRightArrow = ({ onClick }: any) => (
    <button
      onClick={onClick}
      className={`${styles.customArrow} ${styles.rightArrow}`}
      aria-label="Next slide"
    >
      <ArrowForwardIosIcon />
    </button>
  );

  // Create internal add-to-cart handler
  const handleAddToCartInternal = async (product: any) => {
    try {
      const isSimpleProduct = product.type === "simple";

      if (isSimpleProduct) {
        // First, immediately toggle the MiniCart before API call
        if (toggleMiniCart) {
          toggleMiniCart();
        }

        const customizableOptions = product.customizableOptions?.map(
          (option: any) => ({
            id: option.id || `opt-${Date.now()}`,
            type: option.__component || "custom",
            label: option.title || "Custom Option",
            value: option.defaultValue || null,
            priceSurcharge: option.priceSurcharge || 0,
            options: option.options || [],
          })
        );

        const groupingAttributes = product.attributes
          ?.filter((attr: any) => attr.isGroupingAttribute)
          ?.map((attr: any) => ({
            name: attr.name,
            value: attr.values?.[0]?.name || "",
          }));

        // Then, make the API call without passing toggleMiniCart (since we've already called it)
        addToCart(
          product.id,
          1,
          {
            cartCustomId: product.sku || `custom-${Date.now()}`,
            customizableOptions,
            groupingAttributes,
            productData: product, // Pass the full product data for optimistic updates
          },
          dispatch,
          undefined, // Don't pass toggleMiniCart since we already called it
          store.getState // Pass the getState function from the store
        )
          .then(() => {
            console.log("✅ Product added to cart successfully");
          })
          .catch((error) => {
            console.error("❌ Failed to add product to cart:", error);
            // If the API call fails, we could notify the user, but we won't close the cart
            // as the optimistic update is still showing
          });
      } else {
        window.location.href = `/p/${product.url || product.id}`;
      }
    } catch (error) {
      console.error("❌ Failed to add product to cart:", error);
      alert("Failed to add product to cart. Please try again.");
    }
  };

  return (
    <>
      {isLoading ? (
        <ProductSliderSkeleton itemCount={sliderItems} /> // Use the skeleton loader
      ) : (
        <div
          className={`${styles.sliderWrapper} ${
            allContentReady ? styles.contentReady : ""
          }`}
        >
          <Carousel
            swipeable
            draggable={false} // Disable dragging by default
            responsive={responsive}
            ssr={true}
            infinite={false}
            keyBoardControl={true}
            containerClass={styles.sliderContainer}
            itemClass={styles.productCard}
            renderButtonGroupOutside={true}
            customLeftArrow={<CustomLeftArrow />} // Ensure custom arrows are passed
            customRightArrow={<CustomRightArrow />} // Ensure custom arrows are passed
            arrows={true}
            afterChange={(previousSlide, { currentSlide }): void => {
              console.log("ProductSliderComponent.tsx afterChange triggered");
              setTabIndexOnHiddenCarouselItems();
            }}
          >
            {formattedProducts.map((product) => (
              <div key={product.id} className={styles.productCard}>
                <a
                  href={product.url}
                  className={styles.productLink}
                  title={`Ver detalles de ${product.name}`}
                  aria-label={`Ver detalles de ${product.name}. Ver producto: ${
                    product.name
                  }, ${
                    product.priceRange
                      ? product.priceRange
                      : product.centralPrice
                  }${product.oldPrice ? `, antes ${product.oldPrice}` : ""}`}
                >
                  <div className={styles.productImageContainer}>
                    {product.expressDelivery && (
                      <span
                        className={styles.expressOverlay}
                        aria-label="Entrega Express disponible"
                      >
                        Express
                      </span>
                    )}
                    <ProductImage
                      src={product.image}
                      alt={`Imagen de ${product.name}`}
                      className={styles.productImage}
                      title={product.name}
                      loading="lazy"
                      onLoad={() => handleImageLoad(product.id)}
                      onError={() => handleImageError(product.id)}
                      width="175"
                      height="175"
                    />
                  </div>

                  <div className={styles.productCardContent}>
                    <div className={styles.priceSection}>
                      <p
                        className={`${styles.centralPrice} ${
                          product.oldPrice ? styles.discountedPrice : ""
                        }`}
                      >
                        {product.centralPrice}
                      </p>
                      {product.oldPrice ? (
                        <p
                          className={styles.oldPrice}
                          aria-label="Precio anterior"
                        >
                          {product.oldPrice}
                        </p>
                      ) : (
                        <div className={styles.pricePlaceholder}></div>
                      )}
                      {product.priceRange && (
                        <p
                          className={styles.priceRange}
                          aria-label="Rango de precios"
                        >
                          {product.priceRange}
                        </p>
                      )}
                    </div>

                    {/* Only render this div if there is actual delivery content */}
                    {product.fastestDeliveryDate && (
                      <div className={styles.deliveryInfo} aria-live="polite">
                        <span>{product.fastestDeliveryDate}</span>
                      </div>
                    )}

                    <p className={styles.productName}>{product.name}</p>

                    {product.colors?.length > 0 && (
                      <div
                        className={styles.colorSwatches}
                        aria-label={`${product.colors.length} colores disponibles`}
                      >
                        {product.colors.map((color: string, index: number) => (
                          <span
                            key={index}
                            className={styles.colorSwatch}
                            style={{ backgroundColor: color }}
                            title={`Color ${index + 1}`}
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </a>

                <button
                  className="buttonProdMini"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (product.hasChildren) {
                      window.location.href = product.url;
                    } else {
                      // Use external handler if provided, otherwise use internal
                      if (onAddToCart) {
                        onAddToCart(product.fullProduct);
                      } else {
                        handleAddToCartInternal(product.fullProduct);
                      }
                    }
                  }}
                  aria-label={
                    product.hasChildren
                      ? `Ver opciones de ${product.name}`
                      : `Añadir ${product.name} al carrito`
                  }
                  title={
                    product.hasChildren ? `Ver opciones` : `Añadir al carrito`
                  }
                >
                  {!product.hasChildren && (
                    <AddIcon className="icon" aria-hidden="true" />
                  )}
                  {product.hasChildren ? "Opciones" : "Añadir"}
                </button>
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </>
  );
};

export default ProductSlider;
